import {
  ActiveSyllabusItemTypeDto,
  DeprecatedSyllabusItemTypeDto,
  SyllabusItemTypeDto
} from '../apis/sherpath-syllabus-service/sherpath-syllabus-service.constants';
import { RecContentItemTypeDto } from '../apis/rec-gateway/rec-gateway.dtos';
import { AssignmentType } from '../apis/eols-assessment-service/eols-assessment-service.dtos';

export enum ResourceCategory {
  QUIZZES_ASSESSMENTS = 'QUIZZES_ASSESSMENTS',
  CONTENT_RESOURCES = 'CONTENT_RESOURCES',
  CLINICAL_RESOURCES = 'CLINICAL_RESOURCES',
}

export enum SyllabusItemAction {
  ADD_A_FOLDER = 'ADD_A_FOLDER',
  ADD_ADAPTIVE_LESSON = 'ADD_ADAPTIVE_LESSON',
  ADD_ADAPTIVE_QUIZ = 'ADD_ADAPTIVE_QUIZ',
  ADD_EBOOK_READING = 'ADD_EBOOK_READING',
  ADD_MORE_RESOURCES = 'ADD_MORE_RESOURCES',
  ADD_SHERPATH_LESSONS = 'ADD_SHERPATH_LESSONS',
  ADD_SHERPATH_SIMULATIONS = 'ADD_SHERPATH_SIMULATIONS',
  ADD_SHERPATH_SKILLS = 'ADD_SHERPATH_SKILLS',
  ADD_INTERACTIVE_REVIEW = 'ADD_INTERACTIVE_REVIEW',
  ADD_EVOLVE_INSTRUCTOR_RESOURCES = 'ADD_EVOLVE_INSTRUCTOR_RESOURCES',
  ADD_EVOLVE_STUDENT_RESOURCES = 'ADD_EVOLVE_STUDENT_RESOURCES',
  ADD_SHERPATH_GROUP_ACTIVITY = 'ADD_SHERPATH_GROUP_ACTIVITY',
  ADD_SIMCHART = 'ADD_SIMCHART',
  ADD_SHADOW_HEALTH = 'ADD_SHADOW_HEALTH',
  ADD_ASSESSMENT_BUILDER = 'ADD_ASSESSMENT_BUILDER',
  ADD_SHERPATH_POWERPOINT = 'ADD_SHERPATH_POWERPOINT',
  ADD_SHERPATH_CASE_STUDY = 'ADD_SHERPATH_CASE_STUDY',
  ADD_EXTERNAL_LINK = 'ADD_EXTERNAL_LINK',
  ADD_OSMOSIS_VIDEO = 'ADD_OSMOSIS_VIDEO',
  ADD_PRE_SIMULATION_QUIZ = 'ADD_PRE_SIMULATION_QUIZ',
  ADD_POST_SIMULATION_QUIZ = 'ADD_POST_SIMULATION_QUIZ',
  ADD_PATIENT_REPORT = 'ADD_PATIENT_REPORT',
  ADD_LAB_SIMULATION_IMPLEMENTATION_MODULE = 'ADD_LAB_SIMULATION_IMPLEMENTATION_MODULE',
  ADD_VR_SIMULATION_IMPLEMENTATION_MODULE = 'ADD_VR_SIMULATION_IMPLEMENTATION_MODULE',
  ADD_SCMO_NG = 'ADD_SCMO_NG',
  ADD_READING_ASSIGNMENT = 'ADD_READING_ASSIGNMENT',
  BULK_MOVE_REORDER = 'BULK_MOVE_REORDER',
  BULK_REMOVE = 'BULK_REMOVE',
  BULK_UNASSIGN = 'BULK_UNASSIGN',
  BULK_EDIT_SETTING = 'BULK_EDIT_SETTING',
  BULK_MAKE_VISIBLE_NOW = 'BULK_MAKE_VISIBLE_NOW',
  DEV_ADD_ASSIGNMENT = 'DEV_ADD_ASSIGNMENT',
  MOVE = 'MOVE',
  REMOVE = 'REMOVE',
  RENAME = 'RENAME',
  SETTINGS = 'SETTINGS',
  UNASSIGN = 'UNASSIGN',
  VIEW = 'VIEW',
  MAKE_VISIBLE_NOW = 'MAKE_VISIBLE_NOW',
  VIEW_PERFORMANCE = 'VIEW_PERFORMANCE',
  CREATE_DEEP_LINK = 'CREATE_DEEP_LINK',
  SELECT_ALL = 'SELECT_ALL',
  DESELECT_ALL = 'DESELECT_ALL',
  ADD_HESI_ASSESS = 'ADD_HESI_ASSESS',
}

export enum SyllabusItemConfigType {
  STATIC = 'STATIC', // Items that have a consistent contentId and can be found in the resource library
  CONFIGURABLE = 'CONFIGURABLE' // Items that are only addable and completely configurable by the user
}

export type SyllabusItemTypeConfig = {
  icon: string;
  displayName: string;
  iconPrefix: 'gizmo' | 'hmds';
  title: string;
  sortOrder: number;
  recContentType: RecContentItemTypeDto;
  assignmentTypes: AssignmentType[];
  syllabusItemAction: SyllabusItemAction;
  configType: SyllabusItemConfigType;
  resourceCategory: ResourceCategory;
}

const SIMULATION_EXERCISE_ICON = 'simulation-exercise';
const SIMULATION_EXERCISE_LABEL = 'Simulation Exercise';

export const SyllabusItemTypeConfigMap: Record<SyllabusItemTypeDto, SyllabusItemTypeConfig> = {

  [ActiveSyllabusItemTypeDto.FOLDER]: {
    sortOrder: 1,
    recContentType: null,
    icon: null,
    displayName: 'Folder',
    iconPrefix: null,
    title: 'Folder',
    assignmentTypes: null,
    syllabusItemAction: null,
    configType: null,
    resourceCategory: null,
  },
  [ActiveSyllabusItemTypeDto.ADAPTIVE_QUIZ]: {
    sortOrder: 2,
    recContentType: null,
    icon: 'eugene',
    displayName: 'Adaptive Quiz (EAQ)',
    iconPrefix: 'hmds',
    title: 'Adaptive Quiz (EAQ)',
    assignmentTypes: [
      AssignmentType.MASTERY,
      AssignmentType.QUIZ_BY_QUESTION,
      AssignmentType.STANDARD,
      AssignmentType.AUTHESS,
    ],
    syllabusItemAction: SyllabusItemAction.ADD_ADAPTIVE_QUIZ,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.ASSESSMENT_BUILDER_ASSIGNMENT]: {
    sortOrder: 3,
    recContentType: null,
    icon: 'empowering-knowledge',
    displayName: 'Assessment Builder Assignment',
    iconPrefix: 'gizmo',
    title: 'Assessment Builder Assignment',
    assignmentTypes: [AssignmentType.PERSONAL_ASSESSMENT_BUILDER],
    syllabusItemAction: SyllabusItemAction.ADD_ASSESSMENT_BUILDER,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_CASE_STUDY]: {
    sortOrder: 4,
    recContentType: RecContentItemTypeDto.SHERPATH_CASE_STUDY,
    icon: 'case-study',
    displayName: 'Case Study',
    iconPrefix: 'hmds',
    title: 'Case Study',
    assignmentTypes: [AssignmentType.SHERPATH_CASE_STUDY],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_CASE_STUDY,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.CUSTOM_LINK]: {
    sortOrder: 5,
    recContentType: null,
    icon: 'link',
    displayName: 'External Link',
    iconPrefix: 'gizmo',
    title: 'External Link',
    assignmentTypes: [AssignmentType.CUSTOM_LINK],
    syllabusItemAction: SyllabusItemAction.ADD_EXTERNAL_LINK,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.EBOOK_READING]: {
    sortOrder: 6,
    recContentType: RecContentItemTypeDto.EBOOK_READING,
    icon: 'ebook-reading',
    displayName: 'eBook Reading',
    iconPrefix: 'hmds',
    title: 'eBook Reading',
    assignmentTypes: [AssignmentType.EBOOK_READING],
    syllabusItemAction: SyllabusItemAction.ADD_EBOOK_READING,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.EVOLVE_INSTRUCTOR_RESOURCE]: {
    sortOrder: 7,
    recContentType: RecContentItemTypeDto.EVOLVE_RESOURCE,
    icon: 'books',
    displayName: 'Evolve Instructor Resource',
    iconPrefix: 'gizmo',
    title: 'Evolve Instructor Resource',
    assignmentTypes: [AssignmentType.EVOLVE_RESOURCE],
    syllabusItemAction: SyllabusItemAction.ADD_EVOLVE_INSTRUCTOR_RESOURCES,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.EVOLVE_STUDENT_RESOURCE]: {
    sortOrder: 8,
    recContentType: RecContentItemTypeDto.EVOLVE_RESOURCE,
    icon: 'books',
    displayName: 'Evolve Student Resource',
    iconPrefix: 'gizmo',
    title: 'Evolve Student Resource',
    assignmentTypes: [AssignmentType.EVOLVE_RESOURCE],
    syllabusItemAction: SyllabusItemAction.ADD_EVOLVE_STUDENT_RESOURCES,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_GROUP_ACTIVITY]: {
    sortOrder: 9,
    recContentType: RecContentItemTypeDto.SHERPATH_GROUP_ACTIVITY,
    icon: 'couple-of-people',
    displayName: 'Group Activity',
    iconPrefix: 'gizmo',
    title: 'Group Activity',
    assignmentTypes: [AssignmentType.SHERPATH_GROUP_ACTIVITY],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_GROUP_ACTIVITY,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.ADAPTIVE_LESSON]: {
    sortOrder: 10,
    recContentType: RecContentItemTypeDto.ADAPTIVE_LESSON,
    icon: 'lesson',
    displayName: 'Lesson Assignment',
    iconPrefix: 'hmds',
    title: 'Adaptive Lesson',
    assignmentTypes: [AssignmentType.ADAPTIVE_LESSON],
    syllabusItemAction: SyllabusItemAction.ADD_ADAPTIVE_LESSON,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_LESSON]: {
    sortOrder: 11,
    recContentType: RecContentItemTypeDto.SHERPATH_LESSON,
    icon: 'lesson',
    displayName: 'Lesson',
    iconPrefix: 'hmds',
    title: 'Sherpath Lesson',
    assignmentTypes: [AssignmentType.LESSONS],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_LESSONS,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.OSMOSIS_VIDEO]: {
    sortOrder: 12,
    recContentType: RecContentItemTypeDto.OSMOSIS_VIDEO,
    icon: 'play-video',
    displayName: 'Osmosis Video',
    iconPrefix: 'gizmo',
    title: 'Osmosis Video',
    assignmentTypes: [AssignmentType.OSMOSIS_VIDEO],
    syllabusItemAction: SyllabusItemAction.ADD_OSMOSIS_VIDEO,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_POWERPOINT]: {
    sortOrder: 13,
    recContentType: RecContentItemTypeDto.SHERPATH_POWERPOINT,
    icon: 'ppt',
    displayName: 'PowerPoint',
    iconPrefix: 'gizmo',
    title: 'PowerPoint',
    assignmentTypes: [AssignmentType.SHERPATH_POWERPOINT],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_POWERPOINT,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHADOW_HEALTH_ASSIGNMENT]: {
    sortOrder: 14,
    recContentType: RecContentItemTypeDto.SHADOW_HEALTH,
    icon: 'lesson',
    displayName: 'Shadow Health Assignment',
    iconPrefix: 'hmds',
    title: 'Shadow Health Assignment',
    assignmentTypes: [AssignmentType.SHADOW_HEALTH],
    syllabusItemAction: SyllabusItemAction.ADD_SHADOW_HEALTH,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SIMCHART_CASE_STUDY]: {
    sortOrder: 15,
    recContentType: RecContentItemTypeDto.SIM_CHART,
    icon: 'case-study',
    displayName: 'SimChart Case Study',
    iconPrefix: 'hmds',
    title: 'SimChart Case Study',
    assignmentTypes: [AssignmentType.CASE_STUDY],
    syllabusItemAction: SyllabusItemAction.ADD_SIMCHART,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SIMCHART_CHART]: {
    sortOrder: 16,
    recContentType: null,
    icon: 'simulation-nursing',
    displayName: 'SimChart Blank Chart',
    iconPrefix: 'hmds',
    title: 'SimChart Blank Chart',
    assignmentTypes: [AssignmentType.CHART],
    syllabusItemAction: SyllabusItemAction.ADD_SIMCHART,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_SIMULATIONS]: {
    sortOrder: 17,
    recContentType: RecContentItemTypeDto.SHERPATH_SIMULATION,
    icon: 'stethoscope',
    displayName: 'Simulation',
    iconPrefix: 'gizmo',
    title: 'Simulation',
    assignmentTypes: [AssignmentType.SIMULATIONS],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_SIMULATIONS,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_SKILL]: {
    sortOrder: 18,
    recContentType: RecContentItemTypeDto.SHERPATH_SKILL,
    icon: 'hand',
    displayName: 'Skill',
    iconPrefix: 'hmds',
    title: 'Skill',
    assignmentTypes: [AssignmentType.SKILL],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_SKILLS,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.INTERACTIVE_REVIEW]: {
    sortOrder: 19,
    recContentType: RecContentItemTypeDto.REVIEW_QUIZ,
    icon: 'insert-question',
    displayName: 'Interactive Review Questions',
    iconPrefix: 'hmds',
    title: 'Interactive Review Questions',
    assignmentTypes: [AssignmentType.INTERACTIVE_REVIEW],
    syllabusItemAction: SyllabusItemAction.ADD_INTERACTIVE_REVIEW,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_QUIZ]: {
    sortOrder: 20,
    recContentType: RecContentItemTypeDto.PRE_SIMULATION_QUIZ,
    icon: 'stethoscope',
    displayName: 'Pre-Simulation Quiz',
    iconPrefix: 'gizmo',
    title: 'Pre-Simulation Quiz',
    assignmentTypes: [AssignmentType.SLS_PRE_SIMULATION_QUIZ],
    syllabusItemAction: SyllabusItemAction.ADD_PRE_SIMULATION_QUIZ,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_QUIZ]: {
    sortOrder: 21,
    recContentType: RecContentItemTypeDto.POST_SIMULATION_QUIZ,
    icon: 'stethoscope',
    displayName: 'Post-Simulation Quiz',
    iconPrefix: 'gizmo',
    title: 'Post-Simulation Quiz',
    assignmentTypes: [AssignmentType.SLS_POST_SIMULATION_QUIZ],
    syllabusItemAction: SyllabusItemAction.ADD_POST_SIMULATION_QUIZ,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.LAB_SIMULATION_IMPLEMENTATION_MODULE]: {
    sortOrder: 22,
    recContentType: null,
    icon: 'stethoscope',
    displayName: 'Lab Simulation Implementation Module',
    iconPrefix: 'gizmo',
    title: 'Lab Simulation Implementation Module',
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_LAB_SIMULATION_IMPLEMENTATION_MODULE,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.VR_SIMULATION_IMPLEMENTATION_MODULE]: {
    sortOrder: 23,
    recContentType: null,
    icon: 'stethoscope',
    displayName: 'VR Simulation Implementation Module',
    iconPrefix: 'gizmo',
    title: 'VR Simulation Implementation Module',
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_VR_SIMULATION_IMPLEMENTATION_MODULE,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SCMO_NG]: {
    sortOrder: 24,
    recContentType: RecContentItemTypeDto.SCMO_NG,
    icon: 'stethoscope',
    displayName: 'SimChart for the Medical Office Assignment',
    iconPrefix: 'gizmo',
    title: 'SimChart for the Medical Office Assignment',
    assignmentTypes: [AssignmentType.SCMO_NG],
    syllabusItemAction: SyllabusItemAction.ADD_SCMO_NG,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.PATIENT_REPORT]: {
    sortOrder: 25,
    recContentType: null,
    icon: 'stethoscope',
    displayName: 'Patient Report',
    iconPrefix: 'gizmo',
    title: 'Patient Report',
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_PATIENT_REPORT,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.READING_ASSIGNMENT]: {
    sortOrder: 26,
    recContentType: RecContentItemTypeDto.READING_ASSIGNMENT,
    icon: 'ebook-reading',
    displayName: 'Reading Assignment',
    iconPrefix: 'hmds',
    title: 'Reading Assignment',
    assignmentTypes: [AssignmentType.SLS_READING_ASSIGNMENT],
    syllabusItemAction: SyllabusItemAction.ADD_READING_ASSIGNMENT,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [DeprecatedSyllabusItemTypeDto.EVOLVE_RESOURCE]: {
    sortOrder: 100,
    recContentType: null,
    icon: 'books',
    displayName: 'Evolve Resource',
    iconPrefix: 'gizmo',
    title: 'Evolve Resource',
    assignmentTypes: null,
    syllabusItemAction: null,
    configType: null,
    resourceCategory: null
  },
  [DeprecatedSyllabusItemTypeDto.SIMCHART]: {
    sortOrder: 100,
    recContentType: null,
    icon: 'simulation-nursing',
    displayName: 'SimChart',
    iconPrefix: 'hmds',
    title: 'SimChart',
    assignmentTypes: null,
    syllabusItemAction: null,
    configType: null,
    resourceCategory: null
  },
  [ActiveSyllabusItemTypeDto.HESI_ASSESS]: {
    sortOrder: 32,
    recContentType: RecContentItemTypeDto.HESI_ASSESS,
    icon: 'insert-question',
    displayName: 'HESI Assess',
    iconPrefix: 'hmds',
    title: 'HESI Assess',
    assignmentTypes: [AssignmentType.HESI_ASSESS],
    syllabusItemAction: SyllabusItemAction.ADD_HESI_ASSESS,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_EXERCISE]: {
    sortOrder: 33,
    recContentType: RecContentItemTypeDto.PRE_SIMULATION_EXERCISE,
    icon: SIMULATION_EXERCISE_ICON,
    displayName: SIMULATION_EXERCISE_LABEL,
    iconPrefix: 'hmds',
    title: SIMULATION_EXERCISE_LABEL,
    assignmentTypes: [AssignmentType.SLS_JOURNALING_EXERCISE],
    syllabusItemAction: null,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_EXERCISE]: {
    sortOrder: 34,
    recContentType: RecContentItemTypeDto.POST_SIMULATION_EXERCISE,
    icon: SIMULATION_EXERCISE_ICON,
    displayName: SIMULATION_EXERCISE_LABEL,
    iconPrefix: 'hmds',
    title: SIMULATION_EXERCISE_LABEL,
    assignmentTypes: [AssignmentType.SLS_JOURNALING_EXERCISE],
    syllabusItemAction: null,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.JOURNAL_EXERCISE]: {
    sortOrder: 35,
    recContentType: RecContentItemTypeDto.JOURNAL_EXERCISE,
    icon: SIMULATION_EXERCISE_ICON,
    displayName: SIMULATION_EXERCISE_LABEL,
    iconPrefix: 'hmds',
    title: SIMULATION_EXERCISE_LABEL,
    assignmentTypes: [AssignmentType.SLS_JOURNALING_EXERCISE],
    syllabusItemAction: null,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.SBAR_EXERCISE]: {
    sortOrder: 36,
    recContentType: RecContentItemTypeDto.SBAR_EXERCISE,
    icon: SIMULATION_EXERCISE_ICON,
    displayName: SIMULATION_EXERCISE_LABEL,
    iconPrefix: 'hmds',
    title: SIMULATION_EXERCISE_LABEL,
    assignmentTypes: [AssignmentType.SLS_JOURNALING_EXERCISE],
    syllabusItemAction: null,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.VERBAL_REPORT_TEMPLATE]: {
    sortOrder: 37,
    recContentType: RecContentItemTypeDto.VERBAL_REPORT_TEMPLATE,
    icon: '',
    displayName: 'Verbal Report Template',
    iconPrefix: 'hmds',
    title: 'Verbal Report Template',
    assignmentTypes: [AssignmentType.SLS_VERBAL_REPORT_TEMPLATE],
    syllabusItemAction: null,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.OBSERVER_EVALUATION_RUBRIC]: {
    sortOrder: 38,
    recContentType: RecContentItemTypeDto.OBSERVER_EVALUATION_RUBRIC,
    icon: '',
    displayName: 'Observer Evaluation Rubric',
    iconPrefix: 'hmds',
    title: 'Observer Evaluation Rubric',
    assignmentTypes: [AssignmentType.SLS_OBSERVER_EVALUATION_RUBRIC],
    syllabusItemAction: null,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.PERFORMANCE_CHECKLIST]: {
    sortOrder: 39,
    recContentType: RecContentItemTypeDto.PERFORMANCE_CHECKLIST,
    icon: '',
    displayName: 'Performance Checklist',
    iconPrefix: 'hmds',
    title: 'Performance Checklist',
    assignmentTypes: [AssignmentType.SLS_PERFORMANCE_CHECKLIST],
    syllabusItemAction: null,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  }
};

export enum UnSupportedEvolveResourceTypeDEPRECATED {
  LMS = 'LMS',
  DOWNLOAD_BY_RESOURCE_TYPE = 'Download by Resource Type',
  DOWNLOADS = 'downloads'
}

export enum DefaultCoursePlanEvolveResourceTypeDEPRECATED {
  CASE_STUDIES = 'Case studies',
  CASE_STUDY = 'Case study',
  TEACH = 'TEACH',
  PPT = 'PPT',
}
